import { useEffect, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Clock,
  FileDown,
  Loader2,
  Minus,
  Package,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BaseSheet } from '@/components/common/BaseSheet';
import {
  useAddDesignsToPrintOrder,
  usePrintOrder,
  useSendPrintOrder,
  useStartNewPrintOrder,
  useUpdatePrintOrder,
} from '@/hooks/activation';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { ASSET_TYPE_INFO, ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { PRINTABLE_ASSET_DEFAULTS, PrintOrderItem } from '@/types/activation/print-order';
import { POSTER_DESIGNS } from '@/components/activation/poster/posterDesigns';
import { downloadPrintSpecSheet } from '@/services/activation/printSpecSheet';
import { PrintOrderItemThumb } from './PrintOrderItemThumb';
import { AddPrintItemsDialog } from './AddPrintItemsDialog';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const DESIGN_ADD_QTY = 10;
const STARTER_POSTER_QTY = 20;
const MAX_PREVIEW_THUMBS = 5;

interface PrintOrderCardProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A quantity field that lets you type a bulk count (e.g. 100) instead of clicking +/- repeatedly. */
const QuantityInput = ({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled: boolean;
  onCommit: (quantity: number) => void;
}) => {
  const [text, setText] = useState(String(value));

  useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Math.round(Number(text));
    if (Number.isFinite(parsed) && parsed >= 1) {
      if (parsed !== value) onCommit(parsed);
      setText(String(parsed));
    } else {
      setText(String(value));
    }
  };

  return (
    <Input
      type="number"
      min={1}
      inputMode="numeric"
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      aria-label="Quantity"
      className="h-7 w-14 px-1 text-center text-sm font-medium"
    />
  );
};

export const PrintOrderCard = ({ pack, brand = '#4E9CFF' }: PrintOrderCardProps) => {
  const { data: order, isLoading } = usePrintOrder(pack.id);
  const {
    mutate: updateOrder,
    mutateAsync: updateOrderAsync,
    isPending: isUpdating,
  } = useUpdatePrintOrder(pack.id, pack.asset_input_revision);
  const { mutate: sendOrder, isPending: isSending } = useSendPrintOrder(
    pack.id,
    ActivationFunnelService.packGenieId(pack),
  );
  const { mutate: startNewOrder, isPending: isStartingNew } = useStartNewPrintOrder(pack.id);
  const { addDesigns: addPosterDesigns, isAdding: isAddingPoster } = useAddDesignsToPrintOrder(pack, 'poster');
  const { addDesigns: addLookbookDesigns, isAdding: isAddingLookbook } = useAddDesignsToPrintOrder(pack, 'lookbook');

  const [vendorEmail, setVendorEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [seededOrderId, setSeededOrderId] = useState<string | null>(null);
  const [isEditingOrder, setIsEditingOrder] = useState(false);

  useEffect(() => {
    setVendorEmail(order?.vendor_email || '');
    setNotes(order?.notes || '');
  }, [order?.id, order?.vendor_email, order?.notes]);

  // A brand-new, untouched order starts with a practical day-one run: 100 QR
  // stickers, 20 posters, and 10 app posters. Never re-add defaults after the
  // customer has deliberately edited the order.
  useEffect(() => {
    if (!order || seededOrderId === order.id) return;
    const appPosterReady = Boolean(pack.generated_assets?.app_poster_pdf?.url);
    const stickerReady = !pack.touchpoints.qr_sticker ||
      Boolean(pack.generated_assets?.sticker_pdf?.url);
    if (!appPosterReady || !stickerReady) return;

    setSeededOrderId(order.id);
    const isUntouched = order.created_at === order.updated_at;
    if (isUntouched) {
      void (async () => {
        // Lookbook pages are placement inspiration, not manufacturing files.
        // Remove the legacy auto-seeded lookbook item from untouched drafts.
        const nextItems = order.items
          .filter((item) => item.assetType !== 'lookbook_pdf')
          .filter((item) => pack.touchpoints.qr_sticker || item.assetType !== 'sticker_pdf')
          .map((item) => ({ ...item }));
        const addCachedDefault = (type: 'sticker_pdf' | 'app_poster_pdf') => {
          const defaults = PRINTABLE_ASSET_DEFAULTS[type];
          const cached = pack.generated_assets?.[type];
          if (!defaults || !cached?.url) return;

          const existing = nextItems.find((item) => item.assetType === type);
          if (existing) existing.quantity = defaults.quantity;
          else {
            nextItems.push({
              id: `asset:${type}`,
              label: defaults.label,
              url: cached.url,
              quantity: defaults.quantity,
              assetType: type,
            });
          }
        };

        if (pack.touchpoints.qr_sticker) addCachedDefault('sticker_pdf');
        addCachedDefault('app_poster_pdf');

        if (JSON.stringify(nextItems) !== JSON.stringify(order.items)) {
          await updateOrderAsync({
            orderId: order.id,
            updates: { items: nextItems },
            expectedUpdatedAt: order.updated_at,
          });
        }

        const hasPoster = nextItems.some((item) => item.assetType === 'poster_pack_pdf');
        if (!hasPoster && POSTER_DESIGNS[0]) {
          await addPosterDesigns([POSTER_DESIGNS[0]], STARTER_POSTER_QTY);
        }
      })().catch((error) => {
        console.error('Failed to prepare the starter print order:', error);
        toast.error('Could not finish preparing the starter print order');
      });
    }
  }, [order, pack.generated_assets, pack.touchpoints.qr_sticker, seededOrderId, addPosterDesigns, updateOrderAsync]);

  const items = order?.items || [];
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const isSent = order?.status === 'sent';
  const isFulfilled = order?.status === 'fulfilled';
  const isSuperseded = order?.status === 'superseded';
  const isServerSending = order?.status === 'sending';
  const isValidVendorEmail = EMAIL_PATTERN.test(vendorEmail.trim());
  const isClosed = isSent || isFulfilled || isServerSending || isSuperseded;

  const saveItems = (nextItems: PrintOrderItem[]) => {
    if (!order || isUpdating) return;
    updateOrder({
      orderId: order.id,
      updates: { items: nextItems },
      expectedUpdatedAt: order.updated_at,
    });
  };

  const handleQuantityChange = (itemId: string, delta: number) => {
    saveItems(
      items.map((item) =>
        item.id === itemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item,
      ),
    );
  };

  const handleQuantitySet = (itemId: string, quantity: number) => {
    saveItems(
      items.map((item) => (item.id === itemId ? { ...item, quantity } : item)),
    );
  };

  const handleRemoveItem = (itemId: string) => {
    saveItems(items.filter((item) => item.id !== itemId));
  };

  const handleAddAsset = (type: 'sticker_pdf' | 'app_poster_pdf') => {
    if (isUpdating) return;
    const cached = pack.generated_assets?.[type];
    if (!cached?.url) {
      toast.error("That file isn't ready yet");
      return;
    }
    if (items.some((item) => item.assetType === type)) return;
    saveItems([
      ...items,
      {
        id: `asset:${type}`,
        label: ASSET_TYPE_INFO[type].label,
        url: cached.url,
        quantity: PRINTABLE_ASSET_DEFAULTS[type]?.quantity ?? 10,
        assetType: type,
      },
    ]);
  };

  const handleFieldBlur = (field: 'vendor_email' | 'notes', value: string) => {
    if (!order || isUpdating) return;
    const current = field === 'vendor_email' ? order.vendor_email || '' : order.notes || '';
    if (value === current) return;
    updateOrder({
      orderId: order.id,
      updates: { [field]: value || null },
      expectedUpdatedAt: order.updated_at,
    });
  };

  const handleSend = () => {
    if (!order || isUpdating) return;
    sendOrder(
      {
        orderId: order.id,
        params: {
          vendorEmail: vendorEmail.trim(),
          notes: notes.trim() || undefined,
          expectedUpdatedAt: order.updated_at,
        },
      },
      { onSuccess: () => setShowSendConfirm(false) },
    );
  };

  const handleDownloadSpecSheet = () => {
    if (!order || items.length === 0) {
      toast.error('Add something to the order first');
      return;
    }
    try {
      downloadPrintSpecSheet(pack, { items, notes: notes.trim() || null });
      // A file left the browser, so this is a download. It becomes a `print`
      // only if the order is actually sent to a vendor.
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'download',
        { asset_type: 'print_spec_sheet' },
      );
      toast.success('Print specification downloaded — take it to any print shop');
    } catch (error) {
      console.error('Failed to build the print specification:', error);
      toast.error("Couldn't build the print specification");
    }
  };

  /** One honest line about where this order actually is, from its real status. */
  const renderStatusLine = () => {
    if (isServerSending) {
      return (
        <div className="rounded-xl border px-4 py-4 flex items-start gap-3 text-sm" style={{ borderColor: `${brand}40`, backgroundColor: `${brand}0D` }}>
          <Loader2 className="w-4 h-4 mt-0.5 animate-spin shrink-0" style={{ color: brand }} />
          <div className="text-gray-700">
            <div className="font-semibold text-gray-900">Sending the production handoff to your printer</div>
            <div className="mt-1">
              This order stays locked until delivery is confirmed. If it remains here, contact support before
              resending so the printer never receives a duplicate order.
            </div>
          </div>
        </div>
      );
    }

    if (isSent || isFulfilled) {
      return (
        <div
          className="rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          style={{ borderColor: `${brand}30`, backgroundColor: `${brand}08` }}
        >
          <div className="flex items-start gap-2.5 text-sm text-gray-700">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: brand }} />
            <div>
              <div className="font-semibold text-gray-900">
                {isFulfilled ? 'Order fulfilled' : 'Order sent to your printer'}
              </div>
              <div className="mt-0.5">
                {items.length} {items.length === 1 ? 'item' : 'items'} · {totalQuantity} units emailed to{' '}
                <CopyableEmail email={order?.vendor_email} className="font-bold" />
                {order?.sent_at && ` on ${new Date(order.sent_at).toLocaleDateString()}`}.
                {isFulfilled
                  ? ' Marked fulfilled.'
                  : ' Waiting on the printer to confirm proof, price and delivery.'}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => startNewOrder()}
            disabled={isStartingNew}
            className="shrink-0"
          >
            {isStartingNew ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Start a new order
          </Button>
        </div>
      );
    }

    if (isSuperseded) {
      return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2.5 text-sm text-gray-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" />
            <div>
              <div className="font-semibold text-gray-900">This draft was replaced</div>
              <div className="mt-0.5">
                Your pack artwork changed after this draft was built, so it was retired to stop the old files
                reaching a printer. Start a new order to use the current artwork.
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => startNewOrder()}
            disabled={isStartingNew}
            className="shrink-0"
          >
            {isStartingNew ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Start a new order
          </Button>
        </div>
      );
    }

    return null;
  };

  const statusBadgeLabel = isFulfilled
    ? 'Fulfilled'
    : isSent
      ? 'Sent'
      : isServerSending
        ? 'Sending'
        : isSuperseded
          ? 'Replaced'
          : 'Ready to send';

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-8 py-4 md:py-5 border-b flex items-center justify-between gap-4"
          style={{ background: `${brand}05`, borderColor: `${brand}15` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${brand}12` }}>
              <GenieIcon name="clipboard-check" size={26} color={brand} secondaryColor="#FF4DB8" label="Order printed packs" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-gray-900">Get it printed</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                We email the production files straight to your printer.
              </p>
            </div>
          </div>
          <Badge
            variant="outline"
            className="shrink-0"
            style={{ color: brand, borderColor: `${brand}55`, backgroundColor: isSent ? `${brand}0D` : undefined }}
          >
            {statusBadgeLabel}
          </Badge>
        </div>

        <div className="px-5 md:px-8 py-5 space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: brand }} />
              <span>Setting up your print order…</span>
            </div>
          ) : (
            <>
              {renderStatusLine()}

              {!isClosed && (
                <>
                  {/* The one-line offer: what we'll print, and one button to send it. */}
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      {items.length > 0 ? (
                        <div className="flex shrink-0 -space-x-3">
                          {items.slice(0, MAX_PREVIEW_THUMBS).map((item) => (
                            <div key={item.id} className="rounded-lg ring-2 ring-white">
                              <PrintOrderItemThumb item={item} pack={pack} boxSize={44} />
                            </div>
                          ))}
                          {items.length > MAX_PREVIEW_THUMBS && (
                            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500 ring-2 ring-white">
                              +{items.length - MAX_PREVIEW_THUMBS}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50">
                          <Package className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-gray-900">
                          {items.length > 0
                            ? `${totalQuantity} printed items, ready to order`
                            : 'Nothing in your order yet'}
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          {items.length > 0 ? (
                            <>
                              {items.length} {items.length === 1 ? 'design' : 'designs'} ·{' '}
                              {order?.vendor_email ? <CopyableEmail email={order.vendor_email} /> : "we'll ask for the email next"}
                            </>
                          ) : 'Add your QR stickers, posters, or app poster to get started.'}
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsEditingOrder((open) => !open)}
                        className="shrink-0"
                      >
                        {isEditingOrder ? 'Done' : 'Edit'}
                        <ChevronDown
                          className={`ml-1.5 h-4 w-4 transition-transform ${isEditingOrder ? 'rotate-180' : ''}`}
                        />
                      </Button>
                      <Button
                        onClick={() => setShowSendConfirm(true)}
                        disabled={items.length === 0 || isUpdating}
                        className="text-white border-0 shrink-0"
                        style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send to my printer
                      </Button>
                    </div>
                  </div>

                  {/* No print vendor? Walk away with something usable anyway. */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
                    <span>No printer yet?</span>
                    <button
                      type="button"
                      onClick={handleDownloadSpecSheet}
                      disabled={items.length === 0}
                      className="inline-flex items-center gap-1.5 font-semibold hover:underline disabled:opacity-50 disabled:hover:no-underline"
                      style={{ color: brand }}
                    >
                      <FileDown className="h-4 w-4" />
                      Download a print specification
                    </button>
                    <span className="text-gray-500">— take it to any local print shop for a quote.</span>
                  </div>

                  {/* Quantities and vendor details, tucked behind the edit toggle. */}
                  {isEditingOrder && (
                    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                        {items.length === 0 ? (
                          <div className="flex items-center gap-2 py-8 text-sm text-gray-500 justify-center">
                            <Package className="w-4 h-4" />
                            <span>No items yet — add your QR stickers, posters, or app poster.</span>
                          </div>
                        ) : (
                          items.map((item) => (
                            <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                              <PrintOrderItemThumb item={item} pack={pack} />
                              <div className="min-w-0 flex-1">
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm font-semibold text-gray-900 hover:underline truncate block"
                                >
                                  {item.label}
                                </a>
                                {item.assetType && (
                                  <div className="text-xs text-gray-500">{item.notes || 'From this activation pack'}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={isUpdating}
                                  onClick={() => handleQuantityChange(item.id, -1)}
                                  aria-label={`Fewer ${item.label}`}
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </Button>
                                <QuantityInput
                                  value={item.quantity}
                                  disabled={isUpdating}
                                  onCommit={(quantity) => handleQuantitySet(item.id, quantity)}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={isUpdating}
                                  onClick={() => handleQuantityChange(item.id, 1)}
                                  aria-label={`More ${item.label}`}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                                disabled={isUpdating}
                                onClick={() => handleRemoveItem(item.id)}
                                aria-label="Remove item"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>

                      <Button type="button" variant="outline" onClick={() => setShowAddDialog(true)} disabled={isUpdating} className="w-full">
                        <Plus className="w-4 h-4 mr-1.5" />
                        Add designs to print
                      </Button>

                      {/* Vendor + notes */}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Printer's email
                          </label>
                          <Input
                            type="email"
                            placeholder="orders@yourprinter.com"
                            value={vendorEmail}
                            disabled={isUpdating}
                            onChange={(e) => setVendorEmail(e.target.value)}
                            onBlur={(e) => handleFieldBlur('vendor_email', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Special instructions (optional)
                          </label>
                          <Textarea
                            placeholder="Paper stock, shipping address, deadline…"
                            value={notes}
                            disabled={isUpdating}
                            rows={1}
                            onChange={(e) => setNotes(e.target.value)}
                            onBlur={(e) => handleFieldBlur('notes', e.target.value)}
                          />
                        </div>
                      </div>

                      <p className="flex items-center gap-1.5 text-xs text-gray-500">
                        {isUpdating ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: brand }} />
                            Saving your changes…
                          </>
                        ) : (
                          <>
                            <Clock className="h-3.5 w-3.5" />
                            Changes save as you make them.
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add items dialog */}
      <AddPrintItemsDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        pack={pack}
        items={items}
        brand={brand}
        onAddPosterDesign={(design) => addPosterDesigns([design], DESIGN_ADD_QTY)}
        isAddingPoster={isAddingPoster}
        onAddLookbookDesign={(design) => addLookbookDesigns([design], DESIGN_ADD_QTY)}
        isAddingLookbook={isAddingLookbook}
        onAddAsset={handleAddAsset}
      />

      <BaseSheet
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        title="Send this order to your printer?"
        description="Review the production handoff before it leaves Help Genie."
        size="sm"
        loading={isSending}
        preventOutsideClick={isSending}
        footer={(
          <div className="flex w-full justify-start gap-2">
            <Button variant="outline" onClick={() => setShowSendConfirm(false)} disabled={isSending}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isSending || isUpdating || !isValidVendorEmail}
              className="text-white"
              style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
            >
              {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {isSending ? 'Sending…' : 'Send order'}
            </Button>
          </div>
        )}
      >
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            We'll email {items.length} {items.length === 1 ? 'item' : 'items'} ({totalQuantity} total units) as
            verified production links to your printer.
          </p>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
              Printer's email
            </label>
            <Input
              type="email"
              placeholder="orders@yourprinter.com"
              value={vendorEmail}
              disabled={isSending}
              onChange={(e) => setVendorEmail(e.target.value)}
              onBlur={(e) => handleFieldBlur('vendor_email', e.target.value)}
            />
            {!isValidVendorEmail && (
              <p className="mt-1.5 text-xs text-gray-500">
                Don't have one? Close this and download the print specification instead — any local print shop
                can quote from it.
              </p>
            )}
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="font-semibold text-gray-900">Ask the printer to confirm</div>
            <div className="mt-1 text-gray-600">
              Final proof, stock and finish, price, lead time, and delivery address.
            </div>
          </div>
          {notes && <p><strong>Special instructions:</strong> {notes}</p>}
        </div>
      </BaseSheet>
    </div>
  );
};
